import React, { useState, ChangeEvent, useEffect, useRef } from 'react';
import { isEqual } from 'lodash';

import ArrowDown from 'icons/ArrowDown';
import { Checkbox } from 'components/app-components/ui/checkbox';
import { MultiSelectProps } from 'components/generic-components/filterMultiSelect/type';
import { customFilterValue } from 'utils/customFilterValue';
import { capitalizeFirstLetter, removeUnderscoresFromString } from 'utils/string';

const CLOSE_ANIMATION_MS = 150;

export const FilterMultiSelect = ({
  title,
  icon,
  filtersList,
  onFilterListChange,
  onSelectAllChange,
  id,
  isReset,
  isFiltersList,
  initialSelectedOptions,
  isRadioButtons,
}: MultiSelectProps<any>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(initialSelectedOptions || []);
  const previousDataRef = useRef(initialSelectedOptions);

  // Keep the dropdown mounted for the duration of the close animation instead of unmounting immediately.
  useEffect(() => {
    if (isOpen) {
      setShouldRenderDropdown(true);
      return;
    }
    const timer = window.setTimeout(() => setShouldRenderDropdown(false), CLOSE_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  const onInputFocus = () => setIsOpen((prevState) => !prevState);

  const onInputBlur = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setIsOpen(false);
  };

  const onOptionListChange = (e: ChangeEvent<HTMLInputElement>, id: string, isRadio?: boolean) => {
    onFilterListChange(e, id, isRadio);
    if (selectedOptions.includes(e.target.value)) {
      return setSelectedOptions((prevState) =>
        prevState.filter((option) => option.toLowerCase() !== e.target.value.toLowerCase()),
      );
    }
    return setSelectedOptions((prevState) => [...prevState, e.target.value]);
  };

  const allValues = filtersList.map((filter) => (typeof filter === 'object' ? filter.value : filter));
  const isAllSelected = allValues.length > 0 && allValues.every((value) => selectedOptions.includes(value));
  const isSomeSelected = selectedOptions.length > 0 && !isAllSelected;
  const showSelectAll = !isFiltersList && !isRadioButtons && allValues.length > 0;

  const onSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (onSelectAllChange) {
      onSelectAllChange(allValues, checked, id);
    } else {
      // Fallback for callers that only supply a per-option handler: emit one change per value that flips state.
      const changedValues = checked
        ? allValues.filter((value) => !selectedOptions.includes(value))
        : allValues.filter((value) => selectedOptions.includes(value));
      changedValues.forEach((value) => {
        onFilterListChange({ target: { value, checked } } as ChangeEvent<HTMLInputElement>, id);
      });
    }
    setSelectedOptions(checked ? [...allValues] : []);
  };

  // When the initialSelectedOptions value is undefined, it results in the initialization of selectedOptions as an empty array [].
  // To resolve this issue, we initialize selectedOptions for the dropdown when initialSelectedOptions is updated.
  useEffect(() => {
    if (!isEqual(previousDataRef.current, initialSelectedOptions)) {
      setSelectedOptions(initialSelectedOptions || []);
      previousDataRef.current = initialSelectedOptions;
    }
  }, [initialSelectedOptions, title]);

  useEffect(() => {
    isReset && setSelectedOptions([]);
  }, [isReset]);

  return (
    <div className="w-fit" onBlur={onInputBlur} tabIndex={0}>
      <div
        onClick={onInputFocus}
        className={`flex h-10 w-fit cursor-pointer items-center rounded-md border bg-white p-[10px] text-neutral-700 ${
          Boolean(selectedOptions.length) ? 'border-therablue bg-blue-50' : ''
        }`}
      >
        {icon && <div className="pr-4">{icon}</div>}
        <div className={`mr-4 text-sm leading-none text-neutral-700`}>{capitalizeFirstLetter(title.toLowerCase())}</div>
        {Boolean(selectedOptions.length) && !isFiltersList && (
          <div className="mr-2 flex h-[16px] w-[16px] items-center justify-center rounded-[50%] bg-therablue p-[10px] text-[10px] text-white">
            <div>{selectedOptions.length}</div>
          </div>
        )}
        <ArrowDown
          className={`${isOpen ? 'rotate-180' : 'rotate-0'} h-[16px] w-[16px] transition-transform duration-200 ease-out`}
          stroke="currentColor"
        />
      </div>
      {shouldRenderDropdown && (
        <div
          className={`absolute z-[9999] mt-[16px] max-h-[30vh] w-auto min-w-max origin-top overflow-y-auto rounded-[6px] border bg-white p-[12px] pt-0 shadow-lg transition-all duration-150 ease-out md:w-[294px] ${
            isOpen ? 'scale-100 opacity-100' : 'pointer-events-none -translate-y-1 scale-95 opacity-0'
          }`}
        >
          {/* Column names alone read as content types, so say what ticking one does. */}
          <span className="mb-[20px] mt-[12px] block text-[14px]">
            {isFiltersList ? 'Choose what to filter by' : `Filter by ${capitalizeFirstLetter(title.toLowerCase())}`}
          </span>
          {showSelectAll && (
            <div className="mt-3 border-b border-neutral-100 pb-3">
              <Checkbox
                onChange={onSelectAll}
                value="__all__"
                checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
                label="All"
              />
            </div>
          )}
          {filtersList.map((filter) => {
            let label: string;
            let value: string;
            if (typeof filter === 'object') {
              label = filter.label;
              value = filter.value;
            } else {
              label = filter;
              value = filter;
            }
            return (
              <div key={value} className="mt-3">
                {isRadioButtons ? (
                  <>
                    <input
                      type="radio"
                      name="filterRadio"
                      id={`radio-${value}`}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => onOptionListChange(e, id, true)}
                      value={value}
                      checked={selectedOptions.includes(value)}
                    />
                    <label htmlFor={`radio-${value}`} className="ml-2 inline text-sm">
                      {removeUnderscoresFromString(customFilterValue(label), false)}
                    </label>
                  </>
                ) : (
                  <Checkbox
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onOptionListChange(e, id)}
                    value={value}
                    checked={selectedOptions.includes(value)}
                    label={removeUnderscoresFromString(customFilterValue(label), false)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
